import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Button, Dropdown, Row, Spinner, Form } from 'react-bootstrap';
import { defaultTheme, useTheme, useFlowPortalContent } from '../../context/PortalThemeContext';
import './OrderSummary.css';
import './ReturnConfirmationPage.css';
import { ExchangeType } from './ExchangeForVariant';
import { ReturnStates } from './ReturnStates';
import { getDiscountedPrice, ItemCard, VariantCard } from '../shared/ProductVariantCards';
import InfoCard from '../shared/InfoCard';
import EditAddressModal from '../shared/EditAddressModal';
import { fetchWithSessionToken } from '../shared/fetchWithSessionToken';
import { fetchDynamicOrder } from '../../utils/orderLookupApi';
import { CHAT_API_BASE } from '../../config/api';
import { PiArrowLeft, PiCaretDown, PiShield } from 'react-icons/pi';
import { fallbackString } from '../shared/FallbackUtils';
import ReturnPolicyLink from './ReturnPolicyLink';
import ModalOverlay from '../shared/ModalOverlay';
import mockOrderData from '../../mock/mockOrderData.json';
import { useMockStep } from '../../hooks/useMockStep';
import { getDiscountInfo } from '../../utils/discountUtils';
import SummaryItemRow from '../shared/SummaryItemRow';

const ReturnConfirmationPage = ({
    orderDetails,
    updateOrderDetails,
    itemsToReturn,
    itemIdToReturnReasons,
    variantsToExchange,
    customer,
    shopifyProducts,
    onStepChange,
    flowType = 'RETURN'
}) => {
    const exchangeType = variantsToExchange.length > 0 ? ExchangeType.EXCHANGE : ExchangeType.REFUND;
    const theme = useTheme();
    const useMock = useMockStep();
    const defaultSettings = defaultTheme.flowPortalContentSettings.RETURN.review;
    
    // Use the new flow-specific hook
    const flowPortalContent = useFlowPortalContent(flowType);
    const reviewSettings = flowPortalContent?.review || defaultSettings;
    
    const [isLoading, setIsLoading] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editMode, setEditMode] = useState(null);
    const [hasItemsToAdd, setHasItemsToAdd] = useState(false);
    const [appliedStoreCredit, setAppliedStoreCredit] = useState(0);
    const [showStoreCreditControls, setShowStoreCreditControls] = useState(false);
    const [refundMethod, setRefundMethod] = useState('credit'); // 'card', 'credit', or 'split'
    const [selectedImage, setSelectedImage] = useState(null);

    // Idempotency and submission guards
    const submittingRef = useRef(false);
    const requestIdRef = useRef(null);

    const generateRequestId = () => {
        try {
            if (typeof window !== 'undefined' && window.crypto && window.crypto.randomUUID) {
                return window.crypto.randomUUID();
            }
        } catch (e) { /* noop */ }
        return 'rid_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
    };

    // Calculate total order value and discounts
    const totalOrderPrice = orderDetails?.line_items?.reduce((sum, item) => sum + (parseFloat(item.price) * item.quantity), 0) ?? 0;
    const totalOrderDiscount = parseFloat(orderDetails?.current_total_discounts ?? 0);
    const actualOrderTotal = totalOrderPrice - totalOrderDiscount;

    // Create a map of item IDs to their actual prices (with discounts applied)
    const itemIdToActualPrice = useMemo(() => {
        const priceMap = new Map();
        itemsToReturn.forEach(item => {
            // console.log("mapping...")
            const orderLineItem = orderDetails.line_items.find(lineItem =>
                lineItem.id === item.id || lineItem.id === parseInt(item.id)
            );

            if (!orderLineItem) {
                // Fallback to original price if line item not found
                priceMap.set(item.id, parseFloat(item.price));
            } else if (orderLineItem.discount_allocations !== undefined && Array.isArray(orderLineItem.discount_allocations)) {
                // Use discount allocation calculation
                const discountInfo = getDiscountInfo(orderLineItem);
                priceMap.set(item.id, discountInfo.discountedPrice);
            } else {
                // Fallback to legacy logic
                const itemPrice = parseFloat(orderLineItem.price);
                const proportionalDiscount = totalOrderDiscount * (itemPrice / totalOrderPrice);
                priceMap.set(item.id, (itemPrice - proportionalDiscount));
            }
        });
        return priceMap;
    }, [itemsToReturn, orderDetails, totalOrderDiscount, totalOrderPrice]);


    // Get previously returned items total (if any)
    const previouslyReturnedTotal = (orderDetails?.orderReturns ?? []).reduce((sum, ret) => {
        return sum + (ret.returnedItems?.reduce((itemSum, item) => itemSum + (parseFloat(item.itemPrice) * item.quantity), 0) ?? 0);
    }, 0);

    // Calculate current return items total (original prices)
    const currentReturnOriginalPriceTotal = itemsToReturn.reduce((sum, item) => sum + (parseFloat(item.price) * (item.returnQuantity || 1)), 0);

    // Calculate accurate refund amount using the price map
    const actualRefundAmount = itemsToReturn.reduce((sum, item) => {
        const actualPrice = (itemIdToActualPrice.get(item.id) * item.returnQuantity || 1) ?? 0;
        // console.log('item actual price', actualPrice)

        return sum + actualPrice;
    }, 0);
    const returnItemsTotalDiscount = currentReturnOriginalPriceTotal - actualRefundAmount;

    useEffect(() => {
        // Reset applied store credit when refund method changes
        if (refundMethod === 'card') {
            setAppliedStoreCredit(0);
        } else if (refundMethod === 'credit' && actualRefundAmount) {
            setAppliedStoreCredit(actualRefundAmount);
        }
    }, [refundMethod, actualRefundAmount]);

    const refreshOrderDetails = async (orderId, shopId, customerEmail, updateOrderDetails) => {
        try {
            const response = await fetchDynamicOrder({
                companyId: shopId,
                searchParam: orderId,
                additionalParam: customerEmail || orderDetails?.shipping_address?.zip || orderDetails?.shipping_address?.phone,
            });

            if (!response.ok) throw new Error('Failed to fetch order');

            const data = await response.json();
            const enrichedOrder = data.order;

            // Ensure we have all the necessary data
            if (!enrichedOrder.customer && Array.isArray(data.orders) && data.order?.customer) {
                enrichedOrder.customer = data.order.customer;
            }

            // Update order with all necessary data
            enrichedOrder.orderId = orderId;
            enrichedOrder.orderReturns = data.orderReturns;
            enrichedOrder.trackingInfo = data.trackingInfo;
            enrichedOrder.shopifyProducts = data.shopifyProducts;
            enrichedOrder.bearerToken = data.bearerToken;
            enrichedOrder.shopId = shopId;

            if (data.bearerToken && enrichedOrder.order_number) {
                localStorage.setItem('sessionToken', data.bearerToken);
                localStorage.setItem('orderId', enrichedOrder.order_number);
                localStorage.setItem('shopId', shopId);
                localStorage.setItem('userEmail', customerEmail);
            }

            localStorage.setItem('brella_order', JSON.stringify(enrichedOrder));
            updateOrderDetails(enrichedOrder);
        } catch (err) {
            console.error('Error refreshing order:', err);
        }
    };

    // Props
    const SubmitButton = (
        <Button
            onClick={async () => {
                if (submittingRef.current) return;
                submittingRef.current = true;
                setIsLoading(true);
                try {
                    const shopId = orderDetails.shopId;

                    const returnPayload = {
                        shopId,
                        orderId: orderDetails.orderId || orderDetails.order_number || orderDetails.orderNumber,
                        customerEmail: customer.email,
                        type: variantsToExchange.length > 0 ? (variantsToExchange.length > itemsToReturn.length ? 'MIXED' : 'EXCHANGE') : 'REFUND',
                        returnStatus: 'REQUESTED',
                        shippingStatus: 'NONE',
                        refundMethod: priceDifference < 0 ? 'PAYMENT_DUE' : (refundMethod === 'credit' ? 'STORE_CREDIT' : 'ORIGINAL_PAYMENT_METHOD'),
                        claimId: null,
                        notes: '',
                        returnedItems: itemsToReturn.map((item) => {
                            const variant = item;
                            const actualPrice = itemIdToActualPrice.get(item.id);

                            return {
                                itemId: item.id.toString(),
                                itemName: item.name || item.title || '',
                                itemPrice: actualPrice.toString(),
                                itemSrc: item.image_src || variant.image?.src || '',
                                quantity: item.returnQuantity || item.quantity,
                                returnType: item.returnType ?? "REFUND",
                                returnReasons: (itemIdToReturnReasons[item.id] || []).map(reason =>
                                    reason.name + (reason.optionalText ? " - " + reason.optionalText : "")
                                )
                            };
                        }),
                        exchangedItems: variantsToExchange.map(variant => ({
                            itemId: variant.id.toString(),
                            itemName: variant.title || '',
                            itemPrice: variant.price,
                            itemSrc: variant.image_id || variant.image?.src || '',
                            quantity: variant.quantity ?? 1,
                        })),
                        totalAmount: Math.abs(priceDifference).toFixed(2),
                        returnValue: actualRefundAmount,
                        exchangeValue: exchangeValue,
                        shippingCost: shipping,
                        returnFromAddress: {
                            firstName: customer.first_name,
                            lastName: customer.last_name,
                            addressLine: returningFrom.address1,
                            secondaryAddressLine: returningFrom.address2 || '',
                            city: returningFrom.city,
                            state: returningFrom.province_code,
                            zip: returningFrom.zip,
                            country: returningFrom.country_code
                        },
                        shipToAddress: {
                            firstName: customer.first_name,
                            lastName: '',
                            addressLine: shippingTo.address1,
                            secondaryAddressLine: shippingTo.address2 || '',
                            city: shippingTo.city,
                            state: shippingTo.province_code,
                            zip: shippingTo.zip,
                            country: shippingTo.country_code
                        }
                    };

                    if (!requestIdRef.current) {
                        requestIdRef.current = generateRequestId();
                    }
                    const headers = { 'Idempotency-Key': requestIdRef.current };

                    // Check if we have any images to upload
                    const hasImages = itemsToReturn.some(item =>
                        itemIdToReturnReasons[item.id]?.some(reason => reason.images?.length > 0)
                    );

                    let response;
                    if (hasImages) {
                        const formData = new FormData();
                        formData.append('returnData', JSON.stringify(returnPayload));

                        itemsToReturn.forEach(item => {
                            const reasons = itemIdToReturnReasons[item.id] || [];
                            reasons.forEach((reason, reasonIndex) => {
                                if (reason.images && reason.images.length > 0) {
                                    reason.images.forEach((image, imageIndex) => {
                                        formData.append('images', image, `${item.id}_${reasonIndex}_${imageIndex}_${image.name}`);
                                    });
                                }
                            });
                        });

                        response = await fetchWithSessionToken(
                            `${CHAT_API_BASE}/companies/${shopId}/returns/with-images`,
                            {
                                method: 'POST',
                                headers,
                                body: formData,
                            }
                        );
                    } else {
                        response = await fetchWithSessionToken(
                            `${CHAT_API_BASE}/companies/${shopId}/returns/`,
                            {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json', ...headers },
                                body: JSON.stringify(returnPayload),
                            }
                        );
                    }

                    if (!response.ok && response.status !== 409) {
                        console.error('Failed to submit return');
                        alert('There was a problem submitting your return. Please try again.');
                        return;
                    }

                    await refreshOrderDetails(orderDetails.order_number, shopId, customer.email, updateOrderDetails);
                    onStepChange(ReturnStates.ORDER_SUMMARY);
                } catch (err) {
                    console.error('Error submitting return:', err);
                    alert('Unexpected error occurred.');
                } finally {
                    setIsLoading(false);
                    submittingRef.current = false;
                }
            }}
            className="button-accent w-100"
            disabled={isLoading || useMock}
            style={{
                marginTop: '1.5rem',
                fontSize: '1rem',
                opacity: useMock ? 0.5 : 1,
                pointerEvents: isLoading ? 'none' : 'auto'
            }}
        >
            {isLoading ? <Spinner animation="border" size="sm" /> :
                `Submit return & ${exchangeType}${useMock ? " - Disabled for Preview" : ''}`}
        </Button>
    );

    const [returningFrom, setReturningFrom] = useState({
        name: customer.first_name + ' ' + customer.last_name,
        address1: customer.default_address.address1,
        city: customer.default_address.city,
        province_code: customer.default_address.province_code,
        zip: customer.default_address.zip,
        country_code: customer.default_address.country_code
    });

    const [shippingTo, setShippingTo] = useState({ ...returningFrom });

    const handleEditClick = (type) => {
        setEditMode(type);
        setShowEditModal(true);
    };

    const handleAddressSave = (updatedAddress) => {
        if (editMode === 'from') setReturningFrom(updatedAddress);
        if (editMode === 'to') setShippingTo(updatedAddress);
    };

    const HeaderWithDetails = ({ headerText, detailTexts }) => (
        <div className='rc-header-detail-item'>
            <div>
                {headerText}
            </div>
            {(detailTexts ?? []).map((text, index) => (
                <div key={text} className='medium-text' style={{ gap: '0.5rem' }}>
                    <p key={index}>{text}</p>
                </div>
            ))}
        </div>

    );

    const RefundMethodSelector = () => {

        const refundOptions = {
            card: `Back to ${orderDetails.maskedPaymentMethod || 'original payment method'}`,
            credit: 'Store credit',
        };

        return (
            <div className="d-flex flex-column align-items-start mt-3">
                <div >How would you like your refund?</div>
                <Dropdown className="w-100 mt-2" onSelect={(eventKey) => setRefundMethod(eventKey)}>
                    <Dropdown.Toggle
                        variant="light"
                        id="dropdown-basic"
                        bsPrefix='custom-toggle'
                        style={{
                            backgroundColor: 'white',
                            color: theme.textLight,
                            // border: 'none',
                            // whiteSpace: 'normal',
                            // whiteSpaceCollapse: 'preserve-breaks',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            textAlign: 'left',     // makes the label align properly
                            width: '100%'          // optional, keeps layout consistent
                        }}
                    >
                        <span style={{ flex: 1 }}>{refundOptions[refundMethod] || 'Select an option'}</span>
                        <PiCaretDown className="dropdown-caret" />
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        {Object.entries(refundOptions).map(([key, label]) => (
                            <Dropdown.Item style={{ color: theme.textLight }} eventKey={key} key={key}>
                                {label}
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Menu>
                </Dropdown>
            </div>
        );
    }

    useEffect(() => {
        const remainingSelectableItems = orderDetails?.line_items.filter((lineItem) => {
            let isInCurrentReturn = false;
            const lineItemIdStr = String(lineItem.id);
            let quantityAlreadyInReturns = itemsToReturn.reduce((acc, item) => {
                if (String(item.id) === lineItemIdStr) {
                    isInCurrentReturn = true;
                    return acc + (item.returnQuantity || item.quantity);
                }
                return acc;
            }, 0);

            const isInPreviousReturn = Array.isArray(orderDetails.orderReturns) &&
                orderDetails.orderReturns.some(ret =>
                    ret.returnedItems?.some((returnedItem) => {
                        if (String(returnedItem.itemId) === lineItemIdStr) {
                            quantityAlreadyInReturns += (returnedItem.quantity || 1);
                            return true;
                        }
                        return false;
                    }
                    )
                );

            return (!isInCurrentReturn && !isInPreviousReturn) || quantityAlreadyInReturns < lineItem.quantity;
        });

        setHasItemsToAdd((remainingSelectableItems?.length || 0) > 0);
    }, [orderDetails, itemsToReturn]);

    function getProduct(product_id) {
        return shopifyProducts?.find((p) => p.id === product_id);
    };

    function getProductFromVariant(variant_id) {
        if (!shopifyProducts || !Array.isArray(shopifyProducts)) return null;

        return shopifyProducts.find((product) =>
            product.variants?.some((variant) => variant.id === variant_id)
        );
    }

    const AddressCard = ({ title, address, onEdit }) => (
        <InfoCard
            headerText={title}
            details={[
                address?.name || '',
                address?.address1 || '',
                `${address?.city || ''}, ${address?.province_code || ''} ${address?.zip || ''}`,
                address?.country_code || ''
            ]}
            horizontal={true}
            hideImage={true}
            endElements={
                <div className="text-button" onClick={onEdit} style={{
                    color: theme.accentColor,
                    cursor: 'pointer'
                }}>
                    <p>Change</p>
                </div>
            }
        />
    );

    // Calculate shipping cost based on protection status
    const hasProtection = orderDetails.protectionStatus === 'PROTECTED' ||
        orderDetails.lineItems?.some(item =>
            item.vendor === 'Brella Shipping Protection' ||
            item.title?.includes('Protection')
        );
    
    // State for dynamic shipping cost
    const [shipping, setShipping] = useState(hasProtection ? 0.00 : 5.99); // Default return shipping cost when not protected
    const [isCalculatingShipping, setIsCalculatingShipping] = useState(false);

    // Function to calculate shipping cost
    const calculateShippingCost = async () => {
        if (hasProtection) {
            setShipping(0.00);
            return;
        }

        setIsCalculatingShipping(true);
        try {
            const shopId = orderDetails.shopId;
            const requestBody = {
                orderId: orderDetails.orderId || orderDetails.order_number || orderDetails.orderNumber,
                customerEmail: customer.email,
                returnedItems: itemsToReturn.map(item => ({
                    itemId: item.id.toString(),
                    quantity: item.returnQuantity || item.quantity
                })),
                returnFromAddress: {
                    zip: returningFrom.zip
                },
                shipToAddress: {
                    zip: shippingTo.zip
                }
            };

            const response = await fetchWithSessionToken(
                `${CHAT_API_BASE}/companies/${shopId}/returns/calculate-shipping`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(requestBody)
                }
            );

            if (response.ok) {
                const data = await response.json();
                setShipping(data.shippingCost || 5.99);
                // console.log('Shipping cost calculated:', data);
            } else {
                console.error('Failed to calculate shipping cost');
                setShipping(5.99); // Fallback to default
            }
        } catch (error) {
            console.error('Error calculating shipping cost:', error);
            setShipping(5.99); // Fallback to default
        } finally {
            setIsCalculatingShipping(false);
        }
    };

    // Calculate shipping when component mounts or addresses change
    useEffect(() => {
        if (returningFrom.zip && shippingTo.zip && itemsToReturn.length > 0) {
            calculateShippingCost();
        }
    }, [returningFrom.zip, shippingTo.zip, itemsToReturn, hasProtection]);

    const exchangeValue = variantsToExchange.reduce((sum, variant) => sum + (parseFloat(variant.price, 0) * (variant.quantity ?? 1)), 0);
    const newOrderValue = exchangeValue + parseFloat(shipping);
    const priceDifference = actualRefundAmount - newOrderValue;
    const amountOwedText = priceDifference < 0 ? "Payment due" : "Refund";
    const showOriginalAndDiscount = totalOrderDiscount > 0 && currentReturnOriginalPriceTotal + previouslyReturnedTotal > totalOrderPrice;

    // TODO: define in backend or update business logic
    const date = new Date();
    date.setDate(date.getDate() + 14)
    const returnByDate = date.toLocaleDateString();

    // const StoreCreditControls = ({ availableCredit, maxApplicable, label = "Apply store credit" }) => {
    //     const handleStoreCredit = (value) => {
    //         const numValue = parseFloat(value);
    //         if (isNaN(numValue)) return;

    //         // Ensure the value is between 0 and the minimum of available credit and amount due
    //         // Round to 2 decimal places
    //         const boundedValue = Math.min(Math.max(0, numValue), Math.min(availableCredit, maxApplicable));
    //         setAppliedStoreCredit(Number(boundedValue.toFixed(2)));
    //     };

    //     return (
    //         <div className="store-credit-controls" style={{ marginTop: '1rem' }}>
    //             <div className="d-flex justify-content-between align-items-center">
    //                 <Form.Label style={{ marginBottom: 0 }}>{label}</Form.Label>
    //                 <div className="text-muted" style={{ fontSize: '0.875rem' }}>
    //                     Maximum available: ${maxApplicable.toFixed(2)}
    //                 </div>
    //             </div>
    //             <div className="d-flex gap-2 align-items-center" style={{ marginTop: '0.5rem' }}>
    //                 <Form.Control
    //                     type="number"
    //                     value={appliedStoreCredit}
    //                     onChange={(e) => handleStoreCredit(e.target.value)}
    //                     min={0}
    //                     max={maxApplicable}
    //                     step={0.01}
    //                     style={{ maxWidth: '100px' }}
    //                 />
    //                 <Form.Range
    //                     value={appliedStoreCredit}
    //                     onChange={(e) => handleStoreCredit(e.target.value)}
    //                     min={0}
    //                     max={maxApplicable}
    //                     step={0.01}
    //                     style={{ flex: 1 }}
    //                 />
    //                 <Button
    //                     variant="outline-secondary"
    //                     size="sm"
    //                     onClick={() => handleStoreCredit(maxApplicable)}
    //                 >
    //                     Max
    //                 </Button>
    //             </div>
    //         </div>
    //     );
    // };

    const ImageViewerModal = ({ image, onClose }) => {
        if (!image) return null;

        return (
            <ModalOverlay onClose={onClose}>
                <div className="image-viewer-modal">
                    <img
                        src={URL.createObjectURL(image)}
                        alt="Full size return image"
                        style={{ maxWidth: '100%', maxHeight: '90vh', objectFit: 'contain' }}
                    />
                </div>
            </ModalOverlay>
        );
    };

    return (

        <div
            className="d-flex-column"
            style={{
                backgroundColor: theme.background,
                width: '100%',
                maxWidth: '60rem',
                position: 'relative'
            }}
        >

            <Button
                variant="link"
                onClick={() => onStepChange(ReturnStates.ORDER_SUMMARY)}
                style={{
                    position: 'absolute',
                    top: '0rem',
                    left: '0rem',
                    textDecoration: 'underline',
                    textUnderlineOffset: '3px',
                    fontWeight: 600,
                    fontSize: '1.125rem',
                    color: theme.textDark,
                    zIndex: 10
                }}
            >
                <PiArrowLeft className="me-1" size={18} />Cancel
            </Button>

            <h3 className="rc-title">
                {fallbackString(reviewSettings.title, defaultSettings.title)}
            </h3>

            <HeaderWithDetails headerText="Shipping information" />

            <div
                className='d-flex-row'
                style={{
                    backgroundColor: theme.background,
                    color: theme.textDark,
                }}
            >
                {/* Column 1 - Shipping and item info */}
                <div style={{ flex: '2' }}>

                    <div className='d-flex-column flex-md-row' style={{ gap: '1.5rem' }}>
                        <div style={{ flex: 1, width: '100%' }}>
                            <AddressCard title="Returning from" address={returningFrom} onEdit={() => handleEditClick('from')} />
                        </div>
                        <div style={{ flex: 1, width: '100%' }}>
                            <AddressCard title="Shipping to" address={shippingTo} onEdit={() => handleEditClick('to')} />
                        </div>
                    </div>

                    <HeaderWithDetails
                        headerText={fallbackString(reviewSettings.headingTextReturning, defaultSettings.headingTextReturning)}
                        detailTexts={[
                            fallbackString(reviewSettings.bodyTextReturning, defaultSettings.bodyTextReturning),
                            <span style={{ fontWeight: '600' }}>Ship return by {returnByDate}.</span>,
                        ]}
                    />

                    <div
                        className='card'
                        style={{
                            width: '100%',
                            padding: '1rem',
                            marginTop: '.5rem',
                        }}
                    >
                        {itemsToReturn.map((item, index) => (
                            <div key={index + item.id}>
                                <ItemCard
                                    item={item}
                                    shopifyProduct={getProduct(item.product_id)}
                                    horizontal={true}
                                    cardStyle={false}
                                    orderDiscount={totalOrderDiscount}
                                    orderTotal={totalOrderPrice}
                                    flowType="RETURN"
                                />
                                <div className='ms-4'>
                                    {(itemIdToReturnReasons[item.id] || []).length > 0 && (
                                        <div className="return-reason-container">
                                            <p className='medium-text'>
                                                <span style={{ fontWeight: '600' }}>Reason for return: </span>
                                                {(itemIdToReturnReasons[item.id] || []).map((reason, reasonIndex) => (
                                                    <React.Fragment key={reasonIndex}>
                                                        {reason.name + (reason.optionalText ? " - " + reason.optionalText : "")}
                                                        {reason.images && reason.images.length > 0 && (
                                                            <div className="return-images-grid mb-2">
                                                                {reason.images.map((image, imageIndex) => (
                                                                    <div
                                                                        key={imageIndex}
                                                                        className="return-image-container"
                                                                        onClick={() => setSelectedImage(image)}
                                                                    >
                                                                        <img
                                                                            src={URL.createObjectURL(image)}
                                                                            alt={`Return image ${imageIndex + 1}`}
                                                                            className="return-image"
                                                                        />
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </React.Fragment>
                                                ))}
                                            </p>
                                        </div>
                                    )}
                                </div>
                                {index < itemsToReturn.length - 1 && (<div className='mt-3' />)}
                            </div>
                        ))}
                    </div>

                    {hasItemsToAdd && (
                        <Button className='button-outline justify-self-center align-self-center'
                            style={{ marginTop: '1.5rem', fontSize: '1rem' }}
                            onClick={() => onStepChange(ReturnStates.SELECT_RETURN_ITEMS)}
                        >Add another item to your return</Button>)
                    }

                    {exchangeType === ExchangeType.EXCHANGE && (
                        <>
                            <HeaderWithDetails
                                headerText={fallbackString(reviewSettings.headingTextReceiving, defaultSettings.headingTextReceiving)}
                                detailTexts={[fallbackString(reviewSettings.bodyTextReceiving, defaultSettings.bodyTextReceiving)]}
                            />

                            <div
                                className='card'
                                style={{
                                    padding: '1rem',
                                    width: '100%',
                                    marginTop: '.5rem',
                                }}
                            >
                                {variantsToExchange.map((variant, index) => (
                                    <VariantCard key={index + variant.id} variant={variant} shopifyProduct={getProductFromVariant(variant.id)} horizontal={true} cardStyle={false} />
                                ))}
                            </div>
                        </>
                    )}

                </div>

                {/* Column 2 - Summary */}
                <div style={{ flex: '1', minWidth: '15rem' }}>
                    <div className="summary-card">
                        <h2 className="card-header-text" style={{ marginBottom: '1.5rem' }}>
                            Summary
                        </h2>

                        {showOriginalAndDiscount && (
                            <>
                                <SummaryItemRow name="Original item(s) price" amount={currentReturnOriginalPriceTotal.toFixed(2)} />
                                <SummaryItemRow
                                    name="Discount applied"
                                    amount={returnItemsTotalDiscount ?? totalOrderDiscount.toFixed(2)}
                                    sign="-"
                                    className="text-muted"
                                />
                            </>
                        )}

                        <SummaryItemRow name="Return value" amount={actualRefundAmount.toFixed(2)} bold={showOriginalAndDiscount} />

                        {exchangeType === ExchangeType.EXCHANGE && (
                            <SummaryItemRow name="New order" amount={exchangeValue.toFixed(2)} sign='-' />
                        )}

                        <SummaryItemRow 
                            name="Return shipping" 
                            amount={isCalculatingShipping ? "..." : shipping.toFixed(2)} 
                            sign='-' 
                        />

                        {hasProtection && (
                            <div className='card shipping-protection-card'>
                                <PiShield size={30} style={{ flexShrink: 0 }} />
                                <div className='medium-text'>Return label on us! You’re covered.</div>
                            </div>
                        )}

                        {!hasProtection && shipping > 0 && (
                            <div style={{ fontSize: '0.85rem', color: theme.textLight, marginTop: '0.5rem' }}>
                                {isCalculatingShipping ? "Calculating shipping cost..." : "Standard return shipping fee"}
                            </div>
                        )}

                        <div className="divider-horizontal" />

                        {priceDifference > 0 && (<RefundMethodSelector />)}

                        <SummaryItemRow name={amountOwedText} amount={Math.abs(priceDifference).toFixed(2)} bold={true} />

                        {SubmitButton}

                    </div>
                </div>
            </div>

            <EditAddressModal
                show={showEditModal}
                onClose={() => setShowEditModal(false)}
                onSave={handleAddressSave}
                address={editMode === 'from' ? returningFrom : shippingTo}
                titleText={editMode === 'from' ? 'Returning From Address' : 'Shipping To Address'}
            />

            <ImageViewerModal
                image={selectedImage}
                onClose={() => setSelectedImage(null)}
            />

            <ReturnPolicyLink flowType={flowType} />
        </div>

    );
};

export default ReturnConfirmationPage;
